import { useState, useCallback } from 'react';
import { ItemInput } from './components/ItemInput';
import { CategorizationView } from './components/CategorizationView';
import { ReviewView } from './components/ReviewView';
import type { MoscowItem } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { MoscowLive, type MoscowCategory } from '../../components/prioritization/session/liveExperience/liveTools/MoscowLive';

type AppState = 'input' | 'categorizing' | 'review';

// Map categories to priority ranks
const CATEGORY_RANKS: Record<string, number> = {
  'Must': 1,
  'Should': 2,
  'Could': 3,
  'Wont': 4,
};

function MoscowMethodContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession, shouldAutoStart, importSourceLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  
  const [state, setState] = useState<AppState>('input');
  const [items, setItems] = useState<MoscowItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: MoscowItem[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      category: null,
    }));

    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      newSourceIdMap[item.id] = item.sourceId ?? item.id;
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    setItems(prev => [...prev, ...newItems]);

    if (shouldAutoStart) {
      setCurrentItemIndex(0);
      setState('categorizing');
    }
  }, [shouldAutoStart]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by category (Must first, then Should, Could, Won't)
    const sortedItems = [...items]
      .filter(item => item.category !== null)
      .sort((a, b) => CATEGORY_RANKS[a.category!] - CATEGORY_RANKS[b.category!]);
    
    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: idx + 1,
      category: item.category || undefined,
      classificationBreakdown: item.category
        ? {
            moscowCategory: item.category,
          }
        : undefined,
      metadata: {
        moscowCategory: item.category,
      }
    }));
  }, [items, sourceIdMap]);

  const handleStart = (itemNames: string[]) => {
    const newItems: MoscowItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      category: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setState('categorizing');
  };
  
  const handleCategorySelect = (category: 'Must' | 'Should' | 'Could' | 'Wont') => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      category,
    };
    setItems(updatedItems);

    // Move to next item or go to review
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
    } else {
      setState('review');
    }
  };

  const handleMoveItem = (itemId: string, newCategory: 'Must' | 'Should' | 'Could' | 'Wont') => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, category: newCategory } : item
    );
    setItems(updatedItems);
  };

  const handleBack = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
    }
  };

  const handleReset = () => {
    setState('input');
    setItems([]);
    setCurrentItemIndex(0);
    setSourceIdMap({});
  };

  const currentItem = items[currentItemIndex];
  const progress = state === 'categorizing' 
    ? ((currentItemIndex + 1) / items.length) * 100
    : 100;

  const categorizedCount = items.filter(i => i.category != null).length;
  const allCategorized = items.length > 0 && categorizedCount === items.length;

  const handleLiveAssign = (itemId: string, category: MoscowCategory) => {
    setItems(prev =>
      prev.map(item => (item.id === itemId ? { ...item, category } : item))
    );
  };

  return (
    <ToolWrapper
      toolName="MoSCoW Method"
      toolDescription="Categorize items into Must, Should, Could, and Won't Have"
      onImportItems={handleImportItems}
      onGetResults={allCategorized || state === 'review' ? handleGetResults : undefined}
      itemCount={items.length}
      {...props}
    >
      <div className="w-full">
        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              {isAppMode && items.length === 0 && (
                <div className="mb-6 border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
                  <p className="text-sm text-[var(--text-sec)]">
                    {`Import items from your ${importSourceLabel} using the button in the header to start categorizing them with MoSCoW.`}
                  </p>
                </div>
              )}
              {/* Standalone mode - show ItemInput */}
              {!isAppMode && <ItemInput onStart={handleStart} />}
            </>
          )}

          {state === 'categorizing' && isLiveSession && (
            <LiveToolScaffold
              title="MoSCoW"
              subtitle="Assign each item to Must, Should, Could, or Won't"
              progressDone={categorizedCount}
              progressTotal={items.length}
            >
              <MoscowLive
                items={items}
                onAssign={handleLiveAssign}
              />
            </LiveToolScaffold>
          )}

          {state === 'categorizing' && !isLiveSession && currentItem && (
            <CategorizationView
              item={currentItem}
              itemIndex={currentItemIndex}
              totalItems={items.length}
              onCategorySelect={handleCategorySelect}
              onBack={handleBack}
              progress={progress}
            />
          )}

          {state === 'review' && (
            <ReviewView 
              items={items} 
              onMoveItem={handleMoveItem}
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function MoscowMethod(props: ToolHostProps) {
  return <MoscowMethodContent {...props} />;
}
